/**
 * Auth Service - API calls for authentication
 * 
 * Backend endpoints expected:
 * POST /api/auth/login     - Login with email/password
 * POST /api/auth/register  - Register new user
 * POST /api/auth/refresh   - Refresh access token
 * POST /api/auth/logout    - Logout (invalidate refresh token)
 * GET  /api/auth/me        - Get current user profile
 */

import api from './api';
import tokenManager from './tokenManager';

// Development mode flag - uses mock data when backend is unavailable
const USE_MOCK = process.env.REACT_APP_USE_MOCK === 'true'; // Default to mock for now

// Mock users for development
const MOCK_USERS = [
  {
    id: 1,
    email: 'admin@annpriya.com',
    password: 'admin123',
    name: 'Admin User',
    role: 'admin',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=admin'
  },
  {
    id: 2,
    email: 'user@annpriya.com',
    password: 'user123',
    name: 'John Doe',
    role: 'user',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=john'
  }
];

// Generate mock JWT (for development only)
function generateMockToken(user, expiresIn = 3600) {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const payload = btoa(JSON.stringify({
    sub: user.id,
    email: user.email,
    name: user.name,
    role: user.role,
    avatar: user.avatar,
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + expiresIn
  }));
  const signature = btoa('mock-signature');
  return `${header}.${payload}.${signature}`;
}

const authService = {
  /**
   * Login with email and password
   * @param {string} email 
   * @param {string} password 
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  async login(email, password) {
    if (USE_MOCK) {
      // Mock login
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const user = MOCK_USERS.find(
        u => u.email.toLowerCase() === email.toLowerCase() && u.password === password
      );
      
      if (!user) {
        return { success: false, error: 'Invalid email or password' };
      }
      
      // Generate mock tokens
      const accessToken = generateMockToken(user, 3600); // 1 hour
      const refreshToken = generateMockToken(user, 604800); // 7 days
      
      // Store tokens
      tokenManager.setAccessToken(accessToken, 3600);
      tokenManager.setRefreshToken(refreshToken);
      
      // Return user without password
      // eslint-disable-next-line no-unused-vars
      const { password: _password, ...userWithoutPassword } = user;
      return { success: true, user: userWithoutPassword };
    }
    
    // Real API call
    try {
      const response = await api.post('/auth/login', { email, password });
      const { accessToken, expiresIn, user } = response.data;
      // Refresh token is in an httpOnly cookie set by the server
      tokenManager.setAccessToken(accessToken, expiresIn);
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed. Please try again.' 
      };
    }
  },

  /**
   * Admin login — backend rejects any user whose role !== 'admin'.
   * Same response shape as login(); the backend issues the same JWT
   * pair and refresh cookie, so the rest of the auth flow is unchanged.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  async adminLogin(email, password) {
    try {
      const response = await api.post('/auth/admin-login', { email, password });
      const { accessToken, expiresIn, user } = response.data;
      tokenManager.setAccessToken(accessToken, expiresIn);
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed. Please try again.',
      };
    }
  },

  /**
   * Send OTP via Fast2SMS
   * @param {string} phone - Mobile number
   * @returns {Promise<{success: boolean, cooldownSeconds?: number, error?: string}>}
   */
  async sendOtp(phone) {
    try {
      const response = await api.post('/auth/send-otp', { phone });
      return { success: true, ...response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send OTP. Please try again.',
      };
    }
  },

  /**
   * Login with phone number and OTP
   * @param {string} phone - Mobile number
   * @param {string} otp - 6-digit OTP received via SMS
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  async phoneLogin(phone, otp) {
    try {
      const response = await api.post('/auth/phone-login', { phone, otp });
      const { accessToken, expiresIn, user } = response.data;
      // Refresh token is in an httpOnly cookie set by the server
      tokenManager.setAccessToken(accessToken, expiresIn);
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Phone login failed. Please try again.' 
      };
    }
  },

  /**
   * Register with phone number and OTP
   * @param {string} name - User's full name
   * @param {string} phone - Mobile number
   * @param {string} otp - 6-digit OTP received via SMS
   * @param {string} [email] - Optional email address to persist on the new user
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  async phoneRegister(name, phone, otp, email, ageDeclarationAcceptedAt) {
    try {
      const payload = { name, phone, otp };
      if (email && email.trim()) payload.email = email.trim();
      if (ageDeclarationAcceptedAt) payload.ageDeclarationAcceptedAt = ageDeclarationAcceptedAt;
      const response = await api.post('/auth/phone-register', payload);
      const { accessToken, expiresIn, user } = response.data;
      // Refresh token is in an httpOnly cookie set by the server
      tokenManager.setAccessToken(accessToken, expiresIn);
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed. Please try again.'
      };
    }
  },

  /**
   * Check whether a phone number is already registered. Used by the
   * checkout sign-in interception flow to decide between login and register
   * before burning a Firebase OTP.
   * @param {string} phone - E.164 format like +919876543210
   * @returns {Promise<{success: boolean, exists?: boolean, error?: string}>}
   */
  async checkPhoneExists(phone) {
    try {
      const response = await api.post('/auth/phone-exists', { phone });
      return { success: true, exists: !!response.data?.exists };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Could not reach the server. Please try again.',
      };
    }
  },

  /**
   * Register a new user (email/password flow)
   * @param {string} name 
   * @param {string} email 
   * @param {string} password 
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  async register(name, email, password) {
    if (USE_MOCK) {
      // Mock registration
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check if email exists
      if (MOCK_USERS.some(u => u.email.toLowerCase() === email.toLowerCase())) {
        return { success: false, error: 'Email already registered' };
      }
      
      // Create new user
      const newUser = {
        id: MOCK_USERS.length + 1,
        email,
        name,
        role: 'user',
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${email}`
      };
      
      // Generate mock tokens
      const accessToken = generateMockToken(newUser, 3600);
      const refreshToken = generateMockToken(newUser, 604800);
      
      // Store tokens
      tokenManager.setAccessToken(accessToken, 3600);
      tokenManager.setRefreshToken(refreshToken);
      
      return { success: true, user: newUser };
    }
    
    // Real API call
    try {
      const response = await api.post('/auth/register', { name, email, password });
      const { accessToken, expiresIn, user } = response.data;
      tokenManager.setAccessToken(accessToken, expiresIn);
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Registration failed. Please try again.' 
      };
    }
  },

  /**
   * Logout - clear tokens and optionally invalidate on server
   */
  async logout() {
    try {
      if (!USE_MOCK) {
        // Refresh token cookie is cleared by the server
        await api.post('/auth/logout', {});
      }
    } catch (error) {
      // Ignore errors, still clear local tokens
    } finally {
      tokenManager.clearTokens();
    }
  },

  /**
   * Get current user profile
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  async getCurrentUser() {
    if (USE_MOCK) {
      // Get user from token
      const user = tokenManager.getUserFromToken();
      if (user) {
        return { success: true, user };
      }
      return { success: false, error: 'Not authenticated' };
    }
    
    // Real API call
    try {
      const response = await api.get('/auth/me');
      return { success: true, user: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to get user profile' 
      };
    }
  },

  /**
   * Update user profile
   * @param {object} updates 
   * @returns {Promise<{success: boolean, user?: object, error?: string}>}
   */
  async acceptAgeDeclaration() {
    try {
      const response = await api.post('/users/me/age-declaration');
      return { success: true, user: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to save declaration' };
    }
  },

  async updateProfile(updates) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 500));
      const currentUser = tokenManager.getUserFromToken();
      const updatedUser = { ...currentUser, ...updates };
      return { success: true, user: updatedUser };
    }
    
    try {
      const parts = (updates.name || '').trim().split(/\s+/);
      const payload = {
        firstName: parts[0] || '',
        lastName: parts.slice(1).join(' ') || '',
        email: updates.email || undefined,
        phone: updates.phone,
        address: updates.address,
      };
      const response = await api.patch('/users/me', payload);
      return { success: true, user: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to update profile' 
      };
    }
  },

  /**
   * Change password
   * @param {string} currentPassword 
   * @param {string} newPassword 
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async changePassword(currentPassword, newPassword) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to change password'
      };
    }
  },

  /**
   * Set or change the logged-in user's password.
   * - currentPassword: required only when the account already has a password.
   * - email: sent only when the account has none yet (to enable email login).
   * @param {{ currentPassword?: string, newPassword: string, email?: string }} payload
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async setPassword({ currentPassword, newPassword, email }) {
    try {
      await api.post('/auth/set-password', { currentPassword, newPassword, email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to save password',
      };
    }
  },

  /**
   * Request password reset
   * @param {string} email 
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async requestPasswordReset(email) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    }
    
    try {
      await api.post('/auth/forgot-password', { email });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to send reset email' 
      };
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    return tokenManager.hasToken();
  },

  /**
   * Get token manager for direct access
   */
  getTokenManager() {
    return tokenManager;
  }
};

export default authService;
